import { v4 as uuidv4 } from 'uuid'
import {
  Chip,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { GameKind } from '../utils/gameKind'
import { Game, GameStat, useListUserGamesQuery } from '../state/apiSlice'
import { store } from '../state/store'
import { setIsLoading } from '../state/loadingSlice'
import { useEffect, useState } from 'react'
import { setError } from '../state/errorSlice'
import ResultMarker from './ResultMarker'

export default function Stats() {
  const { userId } = useParams()
  const { data: games, error, isLoading } = useListUserGamesQuery(userId!)
  const allColumns = [
    'Focus Test - Phase 1', 'Focus Test - Phase 2',
    'Focus Test - Phase 3', 'Focus Test - Aggregated',
    'Monotonicity Test - Speed', 'Monotonicity Test - Accuracy',
    'Monotonicity Test - Aggregated',
    'Aggregated'
  ]
  const [ columns, setColumns ] = useState([
    'Focus Test - Aggregated', 'Monotonicity Test - Aggregated', 'Aggregated'
  ])

  useEffect(() => {
    store.dispatch(setIsLoading(isLoading))
    if (error) {
      store.dispatch(setError('An unexpected error occured. Please try again!'))
    }
  }, [error, isLoading])

  function transformGames(games: Game[]) {
    return games
      .map(x => x)
      .sort((a, b) => a.Timestamp < b.Timestamp ? 1 : -1)
      .filter(game => game.Stats)
      .map(game => {
        const timestamp = game.Timestamp

        let focusGamePhase1Result = null
        let focusGamePhase2Result = null
        let focusGamePhase3Result = null
        let focusGameAggregatedResult = null
        let monotonityGameSpeedResult = null
        let monotonityGameAccuracyResult = null
        let monotonityGameAggregatedResult = null
        let aggregatedResult = null

        if (game.Stats) {
          const focusGameStats = game.Stats
            .filter(stat => stat.Kind === GameKind.FOCUS_GAME_KIND)
          
          if (focusGameStats.length) {
            const focusGameResults = getFocusGameResult(focusGameStats)
            focusGamePhase1Result = focusGameResults.focusGamePhase1Result
            focusGamePhase2Result = focusGameResults.focusGamePhase2Result
            focusGamePhase3Result = focusGameResults.focusGamePhase3Result
            focusGameAggregatedResult = focusGameResults.focusGameAggregatedResult
          }
          
          const monotonityGames = game.Stats
            .filter(stat => stat.Kind === GameKind.MONOTONITY_GAME_KIND)

          if (monotonityGames.length) {
            const monotonityGameResults = getMonotonityGameResult(monotonityGames)
            monotonityGameSpeedResult = monotonityGameResults.monotonityGameSpeedResult
            monotonityGameAccuracyResult = monotonityGameResults.monotonityGameAccuracyResult
            monotonityGameAggregatedResult = monotonityGameResults.monotonityGameAggregatedResult
          }

          if (focusGameAggregatedResult && monotonityGameAggregatedResult) {
            aggregatedResult = getAggregatedResult(
              focusGameAggregatedResult, monotonityGameAggregatedResult
            )
          }
        }

        return {
          timestamp,
          focusGamePhase1Result,
          focusGamePhase2Result,
          focusGamePhase3Result,
          focusGameAggregatedResult,
          monotonityGameSpeedResult,
          monotonityGameAccuracyResult,
          monotonityGameAggregatedResult,
          aggregatedResult
        }
      })
  }

  function getFocusGameResult(stats: GameStat[]) {
    let focusGamePhase1Result = null
    let focusGamePhase2Result = null
    let focusGamePhase3Result = null
    let focusGameAggregatedResult = null

    if (0 < stats.length) {
      focusGamePhase1Result = getFocusGamePhaseResult(stats[0])
    }
    if (1 < stats.length) {
      focusGamePhase2Result = getFocusGamePhaseResult(stats[1])
    }
    if (2 < stats.length) {
      focusGamePhase3Result = getFocusGamePhaseResult(stats[2])
    }
    focusGameAggregatedResult = (
      focusGamePhase1Result || focusGamePhase2Result || focusGamePhase3Result
    ) ? getFocusGameAggregatedResult(
      focusGamePhase1Result ?? 0,
      focusGamePhase2Result ?? 0,
      focusGamePhase3Result ?? 0
    ) : null

    return {
      focusGamePhase1Result,
      focusGamePhase2Result,
      focusGamePhase3Result,
      focusGameAggregatedResult
    }
  }

  function getMonotonityGameResult(stats: GameStat[]) {
    let monotonityGameSpeedResult = null
    let monotonityGameAccuracyResult = null
    let monotonityGameAggregatedResult = null

    monotonityGameSpeedResult = stats.length ?
      getMonotonityGameSpeedResult(stats[0]) : null
    monotonityGameAccuracyResult = stats.length ?
      getMonotonityGameAccuracyResult(stats[0]) : null
    monotonityGameAggregatedResult = stats.length ?
      getMonotonityGameAggregatedResult(
        monotonityGameSpeedResult ?? 0, monotonityGameAccuracyResult ?? 0
      ) : null
    
    return {
      monotonityGameSpeedResult,
      monotonityGameAccuracyResult,
      monotonityGameAggregatedResult
    }
  }

  function getFocusGamePhaseResult(stat: GameStat) {
    return stat.Hits / (stat.Hits + stat.Misses)
  }

  function getFocusGameAggregatedResult(
    phase1Result: number, phase2Result: number, phase3Result: number
  ) {
    return (phase1Result + phase2Result + phase3Result) / 3
  }

  function getMonotonityGameSpeedResult(stat: GameStat) {
    return (
      stat.Hits + stat.Misses >
      (
        parseFloat(process.env.REACT_APP_MONOTONITY_GAME_EXPECTED_ACCURACY!) *
        parseInt(process.env.REACT_APP_MONOTONITY_GAME_DURATION!) / 1000
      )
    ) ? 1.0 : 0.0
  }

  function getMonotonityGameAccuracyResult(stat: GameStat) {
    return (
      stat.Hits / (stat.Hits + stat.Misses) >
      (parseFloat(process.env.REACT_APP_MONOTONITY_GAME_EXPECTED_ACCURACY!))
    ) ? 1.0 : 0.0
  }

  function getMonotonityGameAggregatedResult(speed: number, accuracy: number) {
    return speed === 1.0 && accuracy === 1.0 ? 1.0 : 0.0
  }

  function getAggregatedResult(
    focusGameAggregatedResult: number, monotonityGameResult: number
  ) {
    return (focusGameAggregatedResult + monotonityGameResult) / 2
  }

  return <>
    <Paper
      className="p-2 my-2"
      component="ul"
    >
      <h2 className="mx-4">Columns:</h2>
      <div className="flex flex-wrap">
        {allColumns.map((column) => {
          return (
            <li className="m-2" key={uuidv4()}>
              <Chip
                label={column}
                color={columns.includes(column) ? 'success' : 'default'}
                onClick={() => {
                  columns.includes(column) ?
                    setColumns(columns.filter(x => x !== column)) :
                    setColumns([...columns, column])
                }}
              />
            </li>
          )
        })}
      </div>
    </Paper>
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="stats table">
        <TableHead>
          <TableRow>
            <TableCell>Timestamp</TableCell>
            {
              columns.includes('Focus Test - Phase 1') ? (
                <TableCell>Focus Test - Phase 1</TableCell>
              ) : null
            }
            {
              columns.includes('Focus Test - Phase 2') ? (
                <TableCell>Focus Test - Phase 2</TableCell>
              ) : null
            }
            {
              columns.includes('Focus Test - Phase 3') ? (
                <TableCell>Focus Test - Phase 3</TableCell>
              ) : null
            }
            {
              columns.includes('Focus Test - Aggregated') ? (
                <TableCell>Focus Test - Aggregated</TableCell>
              ) : null
            }
            {
              columns.includes('Monotonicity Test - Speed') ? (
                <TableCell>Monotonicity Test - Speed</TableCell>
              ) : null
            }
            {
              columns.includes('Monotonicity Test - Accuracy') ? (
                <TableCell>Monotonicity Test - Accuracy</TableCell>
              ) : null
            }
            {
              columns.includes('Monotonicity Test - Aggregated') ? (
                <TableCell>Monotonicity Test - Aggregated</TableCell>
              ) : null
            }
            {
              columns.includes('Aggregated') ? (
                <TableCell>Aggregated</TableCell>
              ) : null
            }
          </TableRow>
        </TableHead>
        <TableBody>
          {
            games?.length ? (
              transformGames(games).map((row: any) => (
                <TableRow
                  key={row.timestamp}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {new Date(row.timestamp).toLocaleString()}
                  </TableCell>
                  {
                    columns.includes('Focus Test - Phase 1') ? (
                      <TableCell>
                        <ResultMarker result={row.focusGamePhase1Result} />
                      </TableCell>
                    ) : null
                  }
                  {
                    columns.includes('Focus Test - Phase 2') ? (
                      <TableCell>
                        <ResultMarker result={row.focusGamePhase2Result} />
                      </TableCell>
                    ) : null
                  }
                  {
                    columns.includes('Focus Test - Phase 3') ? (
                      <TableCell>
                        <ResultMarker result={row.focusGamePhase3Result} />
                      </TableCell>
                    ) : null
                  }
                  {
                    columns.includes('Focus Test - Aggregated') ? (
                      <TableCell>
                        <ResultMarker result={row.focusGameAggregatedResult} />
                      </TableCell>
                    ) : null
                  }
                  {
                    columns.includes('Monotonicity Test - Speed') ? (
                      <TableCell>
                        <ResultMarker result={row.monotonityGameSpeedResult} />
                      </TableCell>
                    ) : null
                  }
                  {
                    columns.includes('Monotonicity Test - Accuracy') ? (
                      <TableCell>
                        <ResultMarker result={row.monotonityGameAccuracyResult} />
                      </TableCell>
                    ) : null
                  }
                  {
                    columns.includes('Monotonicity Test - Aggregated') ? (
                      <TableCell>
                        <ResultMarker result={row.monotonityGameAggregatedResult} />
                      </TableCell>
                    ) : null
                  }
                  {
                    columns.includes('Aggregated') ? (
                      <TableCell>
                        <ResultMarker result={row.aggregatedResult} />
                      </TableCell>
                    ) : null
                  }
                </TableRow>
              
              ))
            ) : (
              <TableRow><TableCell colSpan={columns.length + 1}>This user has no stats yet.</TableCell></TableRow>
            )
          }
        </TableBody>
      </Table>
    </TableContainer>
  </>
}
